import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import useCountryProvinceCache from "../../../../hooks/use-country-province-cache";


interface ProvinceOptionCountry {
    id: number;
    name: string;
    code: string;
}

/**
 * Interface representing a Province option in the autocomplete dropdown.
 * This structure matches the API response format for Province data.
 *
 * @property id - Unique identifier for the Province
 * @property name - Display name of the Province
 * @property city - City where the Province is located
 */
interface ProvinceOption {
    code: string;
    id: number;
    name: string;
    country?: ProvinceOptionCountry;
}

/**
 * Props interface for the ProvinceAutocomplete component.
 * Extends the base autocomplete functionality with Province-specific features.
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected Province option
 * @property onChange - Callback fired when selection changes
 * @property renderOption - Custom render function for dropdown options
 */
interface ProvinceAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<ProvinceOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: ProvinceOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: ProvinceOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * ProvinceAutocomplete Component
 *
 * A searchable dropdown component for selecting Provinces with the following features:
 * - Asynchronous search with debouncing
 * - Custom option rendering with name and city display
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * @component
 */
const ProvinceAutocomplete = <M extends boolean = false, >({
                                                               textFieldProps = {},
                                                               form = false,
                                                               name = undefined,
                                                               required = false,
                                                               setLoadingOptions: setLoadingOptionsProps,
                                                               renderOption: customRenderOption,
                                                               ...props
                                                           }: ProvinceAutocompleteProps<M>) => {
    const {countries} = useCountryProvinceCache(undefined, true);
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Province options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching Province options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        try {
            return countries?.flatMap((country: any) => country.provinces?.map(province => ({
                id: province.id,
                code: province.code,
                name: province.name,
                country: country,
            }))) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, countries]);

    /**
     * Determines if two Province options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: ProvinceOption, value: ProvinceOption | null) =>
        option.id === value?.id || (option.code === value?.code && option.code != null), []);

    /**
     * Extracts the display label from a Province option
     */
    const getOptionLabel = useCallback((option: ProvinceOption) => `${option?.code ?? ""} ${option?.name ? `(${option.name})` : ""}${option.country?.name ? ` - ${option.country.name}` : ""}`.trim(), []);

    /**
     * Gets the unique key for a Province option
     */
    const getOptionKey = useCallback((option: ProvinceOption) => option?.id ?? ((option.code != null) ? `code_${option.code}` : null) ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<ProvinceOption, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            dependencies={[countries]}
            {...props}
        />
    );
};

export default ProvinceAutocomplete;